import { Box, Button, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

import strings, { url } from 'constants/index';
import { UTCSingleDate } from 'utils/formatter.utils';

import { EntityData } from './types';

interface GenericDataSectionProps {
  dataType: string;
  data: EntityData;
  onLoadMore: () => void;
}

export const GenericDataSection = ({ dataType, data, onLoadMore }: GenericDataSectionProps) => {
  if (data.items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No {dataType} found
      </Typography>
    );
  }

  const hasMore = data.items.length < data.totalItems;

  return (
    <Box>
      {data.items.map((item, index) => (
        <Box key={item.id || index} sx={{ mb: 1.5 }}>
          {dataType === 'contracts' ? (
            <>
              <Typography
                component={Link}
                to={getContractItemUrl(item)}
                target="_blank"
                variant="body2"
                sx={{
                  color: 'primary.main',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {getContractLinkText(item)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {getContractDetailsText(item)}
              </Typography>
            </>
          ) : (
            <Typography
              component={Link}
              to={getItemUrl(item, dataType)}
              target="_blank"
              variant="body2"
              sx={{
                color: 'primary.main',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {getItemDisplayText(item, dataType)}
            </Typography>
          )}
        </Box>
      ))}
      {hasMore && (
        <Button variant="outlined" size="small" onClick={onLoadMore} sx={{ mt: 1 }}>
          Load More ({data.items.length} of {data.totalItems})
        </Button>
      )}
    </Box>
  );
};

const getItemDisplayText = (item: any, dataType: string): string => {
  const id = item.id || 'N/A';
  const name = item.name || item.title || item.displayName || 'Unnamed';

  switch (dataType) {
    case 'fixtures':
      return `${name} (ID: ${id}) - ${formatDate(item.startDate)}`;
    case 'seasons':
      return `${name} (ID: ${id}) - ${item.startDate ? formatDate(item.startDate) : ''}`;
    case 'rounds':
      return `${name} (ID: ${id}) - ${item.type || 'Unknown Type'}`;
    case 'competitions':
      return `${name} (ID: ${id}) - ${item.type || 'Unknown Type'}`;
    case 'sports':
    case 'homeCompetitors':
    case 'subVenues':
      return `${name} (ID: ${id})`;
    default:
      return `${name} (ID: ${id})`;
  }
};

const formatDate = (dateString: string): string => {
  if (!dateString) {
    return '';
  }
  try {
    return new Date(dateString).toLocaleDateString();
  } catch {
    return dateString;
  }
};

const getItemUrl = (item: any, dataType: string): string => {
  if (dataType === 'contracts') {
    return getContractItemUrl(item);
  }

  if (dataType === 'homeCompetitors') {
    return item.id ? `${url.base_teams}/${item.id}` : '';
  }

  if (dataType === 'sports') {
    return item.id ? `${url.base_sports}/${item.id}` : '';
  }

  if (dataType === 'subVenues') {
    return item.id ? `${url.base_venues}/${item.id}` : '';
  }

  return `/competition-hierarchy/${dataType}/${item.id}`;
};

type ContractItem = {
  id?: number;
  isActive?: boolean;
  startDate?: string;
  endDate?: string;
  personRole?: string;
  person?: { id?: number; fullName?: string; firstName?: string; lastName?: string };
  competitor?: { id?: number; name?: string; competitorType?: string };
};

const PLAYS_FOR_CONTRACT_ROLES = new Set(['PlaysFor', 'PlaysForDoubles']);

const getContractLinkText = (contract: ContractItem): string =>
  `${getContractDisplayName(contract)} (ID: ${getContractDisplayId(contract)}) - ${
    contract.personRole || 'Unknown Role'
  }`;

const getContractDetailsText = (contract: ContractItem): string => {
  const parts = [getContractStatusLabel(contract)];

  if (contract.personRole && PLAYS_FOR_CONTRACT_ROLES.has(contract.personRole)) {
    if (contract.startDate) {
      parts.push(`${strings.start_date} ${UTCSingleDate(contract.startDate)}`);
    }

    if (contract.endDate) {
      parts.push(`${strings.end_date} ${UTCSingleDate(contract.endDate)}`);
    }
  }

  return parts.join(' - ');
};

const getContractStatusLabel = (contract: ContractItem): string => {
  if (typeof contract.isActive !== 'boolean') {
    return 'Unknown status';
  }

  return contract.isActive ? strings.active : 'Inactive';
};

const getContractDisplayName = (contract: ContractItem): string => {
  if (contract.personRole === 'TrainedBy') {
    const person = contract.person;
    return (
      person?.fullName ||
      [person?.firstName, person?.lastName].filter(Boolean).join(' ') ||
      'Unnamed'
    );
  }

  return contract.competitor?.name || 'Unnamed';
};

const getContractDisplayId = (contract: ContractItem): number | string => {
  if (contract.personRole === 'TrainedBy') {
    return contract.person?.id ?? contract.id ?? 'N/A';
  }

  return contract.competitor?.id ?? contract.id ?? 'N/A';
};

const getContractItemUrl = (contract: ContractItem): string => {
  if (contract.personRole === 'TrainedBy') {
    const personId = contract.person?.id;
    return personId ? `${url.base_persons}/${personId}` : '';
  }

  const competitorId = contract.competitor?.id;
  if (!competitorId) {
    return '';
  }

  switch (contract.personRole) {
    case 'PlaysFor':
      return `${url.base_teams}/${competitorId}`;
    case 'Himself':
      return `${url.base_players}/${competitorId}`;
    case 'PlaysForDoubles':
    case 'DoublePartnership':
      return `${url.base_doubles}/${competitorId}`;
    default:
      switch (contract.competitor?.competitorType) {
        case 'Team':
          return `${url.base_teams}/${competitorId}`;
        case 'Player':
          return `${url.base_players}/${competitorId}`;
        default:
          return '';
      }
  }
};
